import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Radio, Code, Wrench } from 'lucide-react';
import '../styles/animations.css';
import Navbar from '../components/Navbar';

const services = [
  {
    icon: Shield, 
    title: 'Réseaux et Sécurité',
    description: 'Protection de vos infrastructures avec des solutions de sécurité avancées.',
    link: '/services#reseaux-securite',
    bgColor: 'bg-green-100'
  },
  {
    icon: Radio,
    title: 'Télécommunications',
    description: 'Solutions de communication modernes et performantes pour votre entreprise.',
    link: '/services#telecommunications',
    bgColor: 'bg-green-100'
  },
  {
    icon: Code,
    title: 'Développement Web & Mobile',
    description: 'Création d\'applications et sites web sur mesure pour votre activité.',
    link: '/services#developpement',
    bgColor: 'bg-green-100'
  },
  {
    icon: Wrench,
    title: 'Maintenance systèmes et réseaux',
    description: 'Support et maintenance pour garantir la continuité de vos services.',
    link: '/services#maintenance',
    bgColor: 'bg-green-100'
  }
];

const Home = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [selectedService, setSelectedService] = useState<number | null>(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.5;
    }
  }, []);

  return (
    <div className="min-h-screen">
      <Navbar />
      {/* Hero Section */}
      <section className="relative bg-primary text-white py-16 h-[100vh] overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 w-full h-full">
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            autoPlay
            loop
            muted
            playsInline
          >
            <source src="/videos/vid.mov" type="video/mp4" />
            Votre navigateur ne supporte pas la lecture de vidéos.
          </video>
          {/* Overlay sombre pour améliorer la lisibilité */}
          <div className="absolute inset-0 bg-primary/60"></div>
        </div>
        
        {/* Contenu Hero avec position absolute pour le placer où on veut */}
        <div className="absolute top-20 left-0 right-0">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl fade-in-up">
              <h1 className="text-4xl md:text-6xl font-bold leading-tight drop-shadow-lg">
                Sécurisez votre avenir numérique
              </h1>
              <p className="text-xl md:text-2xl leading-relaxed mt-3 drop-shadow-md">
                GoSecuNet vous accompagne dans la transformation et la sécurisation de votre infrastructure IT.
              </p>
              <Link
                to="/contact"
                className="bg-white/90 text-primary hover:bg-white hover:text-primary-dark px-8 py-3 mt-4 rounded-lg text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl inline-block backdrop-blur-sm"
              >
                Contactez-nous
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-green-50 via-white to-green-100">
        {/* Arrière-plan décoratif */}
        <div className="absolute inset-0">
          {/* Effet de bruit */}
          <div className="absolute inset-0 noise-bg"></div>

          {/* Formes morphing */}
          <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-to-r from-green-200/30 to-blue-200/30 animate-morph"></div>
          <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-gradient-to-r from-green-200/30 to-blue-200/30 animate-morph animation-delay-2000"></div>

          {/* Vagues animées */}
          <div className="absolute left-0 right-0 h-32 top-1/3 bg-gradient-to-r from-green-100/20 to-blue-100/20 animate-wave"></div>
          <div className="absolute left-0 right-0 h-32 bottom-1/3 bg-gradient-to-r from-green-100/20 to-blue-100/20 animate-wave animation-delay-2000"></div>

          {/* Particules brillantes */}
          <div className="absolute inset-0">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 bg-white rounded-full animate-sparkle"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 3}s`
                }}
              ></div>
            ))}
          </div>

          {/* Overlay subtil */}
          <div className="absolute inset-0 bg-gradient-to-b from-white/50 via-transparent to-white/50"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 bg-clip-text text-transparent bg-gradient-to-r from-green-600 to-blue-600">
            Nos Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className={`${
                  selectedService === index ? 'bg-green-600 text-white' : 'bg-white/80'
                } glass-effect backdrop-blur-sm rounded-lg p-6 shadow-lg transition-all duration-300 cursor-pointer hover:bg-green-600 hover:text-white group float-animation flex flex-col h-full`}
                style={{ animationDelay: `${index * 200}ms` }}
                onClick={() => setSelectedService(index)}
              >
                <div className="flex-1">
                  <div className="text-gray-700 group-hover:text-white mb-4 group-hover:scale-110 transition-all duration-300">
                    <service.icon size={40} />
                  </div>
                  <h3 className="text-gray-800 text-xl font-semibold mb-3 transition-colors duration-300 group-hover:text-white">{service.title}</h3>
                  <p className="text-gray-600 group-hover:text-white/90 mb-4 transition-colors duration-300">{service.description}</p>
                </div>
                <Link
                  to={service.link}
                  className="w-full inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-gradient-to-r from-red-200 to-red-300 hover:from-red-400 hover:to-red-500 text-red-700 hover:text-white transition-all duration-300 group/link shadow-sm hover:shadow-md font-medium"
                >
                  <span className="group-hover/link:translate-x-1 transition-transform duration-300">En savoir plus</span>
                  <svg
                    className="w-4 h-4 ml-2 transition-transform duration-300 group-hover/link:translate-x-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home; 